import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@components/Card';
import { DashboardCertificates, DashboardCertItem, DashboardCertStatus } from '@/types/dashboard';
import { formatDateBR } from '@pages/financial/financialUtils';
import { certificateBadgeClass, CERTIFICATE_WARNING_DAYS } from '@/utils/certificateStatus';

/**
 * Painel "Certificados Digitais" (admin e finance), no fim do dashboard.
 *
 * Junta num lugar só a validade dos certificados de todas as integrações —
 * cobrança PIX, extrato de conta corrente e o A1 que assina NF-e e e-Social.
 * Cada um deles vence sozinho, sem ninguém mexer em nada, e derruba um fluxo
 * inteiro quando isso acontece; antes deste painel não havia onde ver as datas
 * lado a lado.
 *
 * A partir de 30 dias do vencimento a linha vira uma chamada de renovação com
 * link para a tela certa. Trinta é a janela em que o Itaú aceita renovar. Para
 * o A1 é pouco — a homologação com a Autoridade Certificadora costuma levar
 * mais que isso —, mas ali o aviso serve para COMEÇAR o processo.
 */
export const CertificatesPanel: React.FC<{ data: DashboardCertificates }> = ({ data }) => (
  <Card>
    <CardContent className="p-0">
      {data.expiring === 0 ? (
        <div className="p-4 flex items-start gap-3">
          <ShieldCheck className="text-emerald-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm font-medium text-emerald-800">
              Todos os certificados estão em dia
            </p>
            <p className="text-sm text-slate-600 mt-1">
              Nenhum vence nos próximos {CERTIFICATE_WARNING_DAYS} dias.
            </p>
          </div>
        </div>
      ) : (
        <div className="p-4 border-b border-slate-200 flex items-start gap-3">
          <ShieldAlert className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <p className="text-sm font-medium text-amber-800">
            {data.expiring} certificado(s) precisam de atenção
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left font-medium px-4 py-2">Sistema</th>
              <th className="text-left font-medium px-4 py-2">Empresa / Conta</th>
              <th className="text-left font-medium px-4 py-2">Validade</th>
              <th className="text-left font-medium px-4 py-2">Situação</th>
              <th className="text-right font-medium px-4 py-2">Ação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.items.map((item, i) => (
              <CertificateRow key={`${item.system}-${item.label}-${i}`} item={item} />
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

const CertificateRow: React.FC<{ item: DashboardCertItem }> = ({ item }) => (
  <tr className={item.status === 'ok' ? '' : 'bg-amber-50/40'}>
    <td className="px-4 py-2 text-slate-700">{item.system_label}</td>
    <td className="px-4 py-2">
      <span className="text-slate-800">{item.label}</span>
      {item.document && <span className="text-slate-500 text-xs ml-2">{item.document}</span>}
    </td>
    <td className="px-4 py-2 text-slate-700">
      {item.expires_at ? formatDateBR(item.expires_at) : '—'}
    </td>
    <td className="px-4 py-2">
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${certificateBadgeClass(item.status)}`}
      >
        <StatusIcon status={item.status} />
        {statusLabel(item)}
      </span>
    </td>
    <td className="px-4 py-2 text-right">
      {item.status === 'ok' ? (
        <span className="text-xs text-slate-400">—</span>
      ) : (
        <Link
          to={item.renew_link}
          className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800"
        >
          {item.status === 'missing' || item.status === 'unknown'
            ? 'Enviar certificado'
            : 'Renovar certificado'}
          <ArrowRight size={14} />
        </Link>
      )}
    </td>
  </tr>
);

const StatusIcon: React.FC<{ status: DashboardCertStatus }> = ({ status }) => {
  if (status === 'expired' || status === 'missing') return <ShieldX size={12} />;
  if (status === 'unknown') return <ShieldQuestion size={12} />;
  if (status === 'expiring') return <ShieldAlert size={12} />;
  return <ShieldCheck size={12} />;
};

/**
 * Texto da situação.
 *
 * "Vence hoje" e "vence amanhã" em vez de "0 dia(s)" e "1 dia(s)": nessa faixa
 * a mensagem é o que decide se alguém age agora ou deixa para depois.
 */
const statusLabel = (item: DashboardCertItem): string => {
  switch (item.status) {
    case 'missing':
      return 'Sem certificado';
    case 'unknown':
      // A integração está funcionando; o que falta é o ERP conhecer a data.
      return 'Validade não registrada';
    case 'expired':
      return item.days_remaining === -1 ? 'Venceu ontem' : `Venceu há ${-item.days_remaining} dias`;
    case 'expiring':
      if (item.days_remaining === 0) return 'Vence hoje';
      if (item.days_remaining === 1) return 'Vence amanhã';
      return `Vence em ${item.days_remaining} dias`;
    default:
      return `${item.days_remaining} dias`;
  }
};
